import { ESdChangeReason, IDisplayItem, SpecialDIIds } from "@kuika/kuika-cl-model";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { KIcon } from "../../../../shared/icon/k-icon";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { StaticTableHelper } from "../../../../shared/utility/static-table-helper";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";

/**
 * The table changes whose button is still on screen once they land, so the button is where they are
 * confirmed. Inserting a row is not among them: it selects the new row, and this panel only shows
 * while a cell is selected.
 */
type ConfirmedTableOperation = "insertColumnLeft" | "insertColumnRight" | "clearCell" | "merge" | "unmerge";

const CONFIRMED_OPERATION_LABELS: Record<ConfirmedTableOperation, IKuikaButtonStatusLabels> = {
  insertColumnLeft: { loading: "Inserting", success: "Inserted" },
  insertColumnRight: { loading: "Inserting", success: "Inserted" },
  clearCell: { loading: "Clearing", success: "Cleared" },
  merge: { loading: "Merging", success: "Merged" },
  unmerge: { loading: "Unmerging", success: "Unmerged" }
};

export interface IColumnPosition {
  columnId: number;
  rowIndex: number;
  columnIndex: number;
}

export interface IStaticTableOperationsPanelState {
  mergeSelectionMode: boolean;
  selectedColumnsForMerge: IColumnPosition[];
  /** The operation whose button shows `operationStatus`; every other button stays idle. */
  confirmingOperation: ConfirmedTableOperation | null;
  /** Owned by `SaveStatus`, never set directly. */
  operationStatus: KuikaButtonStatus;
}

class StaticTableOperationsPanel extends PureSdComponent<IWithSDProps, IStaticTableOperationsPanelState> {
  private readonly operationStatus: SaveStatus = new SaveStatus((operationStatus: KuikaButtonStatus): void => {
    this.setState({ operationStatus });
  });

  constructor(props: IWithSDProps) {
    super(props);
    this.state = {
      mergeSelectionMode: false,
      selectedColumnsForMerge: [],
      confirmingOperation: null,
      operationStatus: "idle"
    };
  }

  componentWillUnmount(): void {
    this.operationStatus.dispose();
  }

  /** What `operation`'s button shows: the running operation's status, idle for every other one. */
  private statusOf = (operation: ConfirmedTableOperation): KuikaButtonStatus =>
    this.state.confirmingOperation === operation ? this.state.operationStatus : "idle";

  /**
   * Runs one table change behind the designer's full-screen wait and confirms it on the button that
   * asked for it: its ✓ is held once the wait lifts, then `then` runs. With the changed table in view
   * beside it, that ✓ is the whole confirmation — no toast. A second press while one runs or holds
   * its ✓ does nothing. A failure puts the button back and propagates as it always did.
   */
  private runConfirmedOperation = async (
    operation: ConfirmedTableOperation,
    change: () => void,
    then?: () => void
  ): Promise<void> => {
    if (!this.operationStatus.canStart) return;
    this.setState({ confirmingOperation: operation });
    this.operationStatus.start();
    try {
      await this.props.sd.processWithSpinner(async (): Promise<void> => change());
    } catch (error) {
      this.operationStatus.fail();
      throw error;
    }
    this.operationStatus.succeed(then);
  };

  isStaticTableColumnOrDescendant = (): boolean => {
    const sd = this.props.sd.getScreenDesign();
    const selectedDi = this.props.sd.getSelectedDisplayItem(sd);
    if (!selectedDi) return false;

    let currentDi: IDisplayItem | undefined = selectedDi;
    while (currentDi) {
      if (currentDi.item?.name === "StaticTableColumn") {
        return true;
      }
      if (currentDi.parentId === undefined || currentDi.parentId === SpecialDIIds.rootDI) {
        return false;
      }
      currentDi = this.props.sd.screenDesignDictionary[currentDi.parentId];
    }
    return false;
  };

  getStaticTableColumnKuikaId = (): string | undefined => {
    const sd = this.props.sd.getScreenDesign();
    const selectedDi = this.props.sd.getSelectedDisplayItem(sd);
    if (!selectedDi) return undefined;

    let currentDi: IDisplayItem | undefined = selectedDi;
    while (currentDi) {
      if (currentDi.item?.name === "StaticTableColumn") {
        return currentDi.id.toString();
      }
      if (currentDi.parentId === undefined || currentDi.parentId === SpecialDIIds.rootDI) {
        return undefined;
      }
      currentDi = this.props.sd.screenDesignDictionary[currentDi.parentId];
    }
    return undefined;
  };

  handleInsertRowAbove = async () => {
    const columnKuikaId = this.getStaticTableColumnKuikaId();
    if (!columnKuikaId) return;

    const sd = this.props.sd.getScreenDesign();
    const dict = this.props.sd.screenDesignDictionary;
    const context = StaticTableHelper.getContext(sd, dict, columnKuikaId);
    if (!context) return;

    await this.props.sd.processWithSpinner(async () => {
      const newRowId = StaticTableHelper.insertRow(sd, dict, context, "above");
      this.props.sd.setScreenDesign(sd, ESdChangeReason.DragDropDi, newRowId.toString());
      KMainFunctions.displaySuccessNotification("Row inserted above");
    });
  };

  handleInsertRowBelow = async () => {
    const columnKuikaId = this.getStaticTableColumnKuikaId();
    if (!columnKuikaId) return;

    const sd = this.props.sd.getScreenDesign();
    const dict = this.props.sd.screenDesignDictionary;
    const context = StaticTableHelper.getContext(sd, dict, columnKuikaId);
    if (!context) return;

    await this.props.sd.processWithSpinner(async () => {
      const newRowId = StaticTableHelper.insertRow(sd, dict, context, "below");
      this.props.sd.setScreenDesign(sd, ESdChangeReason.DragDropDi, newRowId.toString());
      KMainFunctions.displaySuccessNotification("Row inserted below");
    });
  };

  handleInsertColumnLeft = async () => {
    const columnKuikaId = this.getStaticTableColumnKuikaId();
    if (!columnKuikaId) return;

    const sd = this.props.sd.getScreenDesign();
    const dict = this.props.sd.screenDesignDictionary;
    const context = StaticTableHelper.getContext(sd, dict, columnKuikaId);
    if (!context) return;

    await this.runConfirmedOperation("insertColumnLeft", (): void => {
      const newColumnId = StaticTableHelper.insertColumn(sd, dict, context, "left");
      if (newColumnId) {
        this.props.sd.setScreenDesign(sd, ESdChangeReason.DragDropDi, newColumnId.toString());
      } else {
        this.props.sd.setScreenDesign(sd, ESdChangeReason.DragDropDi);
      }
    });
  };

  handleInsertColumnRight = async () => {
    const columnKuikaId = this.getStaticTableColumnKuikaId();
    if (!columnKuikaId) return;

    const sd = this.props.sd.getScreenDesign();
    const dict = this.props.sd.screenDesignDictionary;
    const context = StaticTableHelper.getContext(sd, dict, columnKuikaId);
    if (!context) return;

    await this.runConfirmedOperation("insertColumnRight", (): void => {
      const newColumnId = StaticTableHelper.insertColumn(sd, dict, context, "right");
      if (newColumnId) {
        this.props.sd.setScreenDesign(sd, ESdChangeReason.DragDropDi, newColumnId.toString());
      } else {
        this.props.sd.setScreenDesign(sd, ESdChangeReason.DragDropDi);
      }
    });
  };

  handleSplitCell = async () => {
    const columnKuikaId = this.getStaticTableColumnKuikaId();
    if (!columnKuikaId) return;

    const sd = this.props.sd.getScreenDesign();
    const dict = this.props.sd.screenDesignDictionary;
    const context = StaticTableHelper.getContext(sd, dict, columnKuikaId);
    if (!context) return;

    await this.props.sd.processWithSpinner(async () => {
      const focusId = StaticTableHelper.splitCell(sd, dict, context);
      this.props.sd.setScreenDesign(sd, ESdChangeReason.DragDropDi, focusId.toString());
      KMainFunctions.displaySuccessNotification("Cell split successfully");
    });
  };

  handleDeleteRow = async () => {
    const columnKuikaId = this.getStaticTableColumnKuikaId();
    if (!columnKuikaId) return;

    const sd = this.props.sd.getScreenDesign();
    const dict = this.props.sd.screenDesignDictionary;
    const context = StaticTableHelper.getContext(sd, dict, columnKuikaId);
    if (!context) return;

    await this.props.sd.processWithSpinner(async () => {
      const focusId = StaticTableHelper.deleteRow(sd, dict, context);
      if (focusId) {
        this.props.sd.setScreenDesign(sd, ESdChangeReason.DragDropDi, focusId.toString());
      }
    });
  };

  handleDeleteColumn = async () => {
    const columnKuikaId = this.getStaticTableColumnKuikaId();
    if (!columnKuikaId) return;

    const sd = this.props.sd.getScreenDesign();
    const dict = this.props.sd.screenDesignDictionary;
    const context = StaticTableHelper.getContext(sd, dict, columnKuikaId);
    if (!context) return;

    await this.props.sd.processWithSpinner(async () => {
      const focusId = StaticTableHelper.deleteColumn(sd, dict, context);
      if (focusId) {
        this.props.sd.setScreenDesign(sd, ESdChangeReason.DragDropDi, focusId.toString());
      }
    });
  };

  handleDeleteCell = async () => {
    const columnKuikaId = this.getStaticTableColumnKuikaId();
    if (!columnKuikaId) return;

    const sd = this.props.sd.getScreenDesign();
    const dict = this.props.sd.screenDesignDictionary;
    const context = StaticTableHelper.getContext(sd, dict, columnKuikaId);
    if (!context) return;

    await this.runConfirmedOperation("clearCell", (): void => {
      const focusId = StaticTableHelper.deleteCellContent(sd, dict, context);
      this.props.sd.setScreenDesign(sd, ESdChangeReason.DragDropDi, focusId.toString());
    });
  };

  handleToggleMergeMode = () => {
    this.setState((prevState) => ({
      mergeSelectionMode: !prevState.mergeSelectionMode,
      selectedColumnsForMerge: []
    }));
  };

  handleSelectColumnForMerge = () => {
    const columnKuikaId = this.getStaticTableColumnKuikaId();
    if (!columnKuikaId) return;

    const sd = this.props.sd.getScreenDesign();
    const dict = this.props.sd.screenDesignDictionary;
    const context = StaticTableHelper.getContext(sd, dict, columnKuikaId);
    if (!context) return;

    const columnId = parseInt(columnKuikaId, 10);
    const columnPosition: IColumnPosition = {
      columnId,
      rowIndex: context.rowIndex,
      columnIndex: context.columnIndex
    };

    this.setState((prevState) => {
      const isSelected = prevState.selectedColumnsForMerge.some((col) => col.columnId === columnId);
      if (isSelected) {
        return {
          selectedColumnsForMerge: prevState.selectedColumnsForMerge.filter((col) => col.columnId !== columnId)
        };
      }
      return {
        selectedColumnsForMerge: [...prevState.selectedColumnsForMerge, columnPosition]
      };
    });
  };

  handleMergeSelectedColumns = async () => {
    const columnKuikaId = this.getStaticTableColumnKuikaId();
    if (!columnKuikaId) return;

    const sd = this.props.sd.getScreenDesign();
    const dict = this.props.sd.screenDesignDictionary;
    const context = StaticTableHelper.getContext(sd, dict, columnKuikaId);
    if (!context) return;

    if (this.state.selectedColumnsForMerge.length < 2) {
      KMainFunctions.displayWarningNotification("Please select at least two columns to merge.");
      return;
    }

    const direction = StaticTableHelper.determineMergeDirection(context.bodyRows, this.state.selectedColumnsForMerge);

    if (direction === "invalid") {
      KMainFunctions.displayWarningNotification(
        "Selected columns must be in the same row for horizontal merge or in the same column position for vertical merge."
      );
      return;
    }

    // Merge mode, and with it the Merge Cells button, stays until the button's ✓ has been read.
    const leaveMergeMode = (): void => this.setState({ mergeSelectionMode: false, selectedColumnsForMerge: [] });
    await this.runConfirmedOperation(
      "merge",
      (): void => {
        let focusId: number | undefined;

        if (direction === "vertical") {
          focusId = StaticTableHelper.handleMergeColumnsVertically(sd, dict, this.state.selectedColumnsForMerge);
          if (focusId) {
            StaticTableHelper.recalculateAllMergedCellDimensions(context.tableDi, dict);
            this.props.sd.setScreenDesign(sd, ESdChangeReason.DragDropDi, focusId.toString());
          }
        } else {
          const columnIds = this.state.selectedColumnsForMerge.map((col) => col.columnId);
          focusId = StaticTableHelper.handleMergeColumns(sd, dict, context, columnIds);
          if (focusId) {
            StaticTableHelper.recalculateAllMergedCellDimensions(context.tableDi, dict);
            this.props.sd.setScreenDesign(sd, ESdChangeReason.DragDropDi, focusId.toString());
          }
        }
      },
      leaveMergeMode
    );
  };

  handleUnmergeColumns = async () => {
    const columnKuikaId = this.getStaticTableColumnKuikaId();
    if (!columnKuikaId) return;

    const sd = this.props.sd.getScreenDesign();
    const dict = this.props.sd.screenDesignDictionary;
    const context = StaticTableHelper.getContext(sd, dict, columnKuikaId);
    if (!context) return;

    await this.runConfirmedOperation("unmerge", (): void => {
      const focusId = StaticTableHelper.handleUnmergeColumns(sd, dict, context);
      StaticTableHelper.recalculateAllMergedCellDimensions(context.tableDi, dict);
      this.props.sd.setScreenDesign(sd, ESdChangeReason.DragDropDi, focusId.toString());
    });
  };

  render() {
    if (!this.isStaticTableColumnOrDescendant()) return null;

    const columnKuikaId = this.getStaticTableColumnKuikaId();
    const isColumnSelected =
      columnKuikaId && this.state.selectedColumnsForMerge.some((col) => col.columnId === parseInt(columnKuikaId, 10));

    const items = [
      {
        key: "staticTablePanel",
        label: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <KIcon iconName="grid_on" style={{ fontSize: 18, color: "var(--accent-fg)" }} />
            <KuikaTextMedium
              style={{
                overflow: "hidden",
                maxWidth: 140,
                textOverflow: "ellipsis"
              }}
            >
              Table Operations
            </KuikaTextMedium>
          </div>
        ),
        children: (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div
              style={{
                padding: "8px 12px",
                background: "var(--dark-1)",
                borderRadius: "var(--radius-l)",
                border: "1px solid var(--dark-3)"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                <KIcon iconName="add_box" style={{ fontSize: 16, color: "var(--accent-fg)" }} />
                <KuikaTextMedium style={{ fontSize: 12, fontWeight: 600, color: "var(--dark-10)" }}>
                  Insert
                </KuikaTextMedium>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <KuikaTooltip content="Insert a new row above the current cell">
                  <div style={{ flex: 1 }}>
                    <KuikaButton
                      onClick={this.handleInsertRowAbove}
                      type="secondary"
                      size="sm"
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 4, justifyContent: "center" }}
                      tabIndex={0}
                    >
                      <KIcon iconName="arrow_upward" style={{ fontSize: 16 }} />
                      <span>Row</span>
                    </KuikaButton>
                  </div>
                </KuikaTooltip>
                <KuikaTooltip content="Insert a new row below the current cell">
                  <div style={{ flex: 1 }}>
                    <KuikaButton
                      onClick={this.handleInsertRowBelow}
                      type="secondary"
                      size="sm"
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 4, justifyContent: "center" }}
                      tabIndex={0}
                    >
                      <KIcon iconName="arrow_downward" style={{ fontSize: 16 }} />
                      <span>Row</span>
                    </KuikaButton>
                  </div>
                </KuikaTooltip>
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <KuikaTooltip content="Insert a new column to the left">
                  <div style={{ flex: 1 }}>
                    <KuikaButton
                      onClick={this.handleInsertColumnLeft}
                      type="secondary"
                      size="sm"
                      status={this.statusOf("insertColumnLeft")}
                      statusLabels={CONFIRMED_OPERATION_LABELS.insertColumnLeft}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 4, justifyContent: "center" }}
                      tabIndex={0}
                    >
                      <KIcon iconName="arrow_back" style={{ fontSize: 16 }} />
                      <span>Col</span>
                    </KuikaButton>
                  </div>
                </KuikaTooltip>
                <KuikaTooltip content="Insert a new column to the right">
                  <div style={{ flex: 1 }}>
                    <KuikaButton
                      onClick={this.handleInsertColumnRight}
                      type="secondary"
                      size="sm"
                      status={this.statusOf("insertColumnRight")}
                      statusLabels={CONFIRMED_OPERATION_LABELS.insertColumnRight}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 4, justifyContent: "center" }}
                      tabIndex={0}
                    >
                      <KIcon iconName="arrow_forward" style={{ fontSize: 16 }} />
                      <span>Col</span>
                    </KuikaButton>
                  </div>
                </KuikaTooltip>
              </div>
            </div>

            <div
              style={{
                padding: "8px 12px",
                background: "var(--dark-1)",
                borderRadius: "var(--radius-l)",
                border: "1px solid var(--dark-3)"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                <KIcon iconName="delete" style={{ fontSize: 16, color: "var(--red-5)" }} />
                <KuikaTextMedium style={{ fontSize: 12, fontWeight: 600, color: "var(--dark-10)" }}>
                  Delete
                </KuikaTextMedium>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <KuikaTooltip content="Delete the current row">
                  <div style={{ flex: 1 }}>
                    <KuikaButton
                      onClick={this.handleDeleteRow}
                      color="danger"
                      type="secondary"
                      size="sm"
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 4, justifyContent: "center" }}
                      tabIndex={0}
                    >
                      <KIcon iconName="delete_sweep" style={{ fontSize: 16 }} />
                      <span>Row</span>
                    </KuikaButton>
                  </div>
                </KuikaTooltip>
                <KuikaTooltip content="Delete the current column">
                  <div style={{ flex: 1 }}>
                    <KuikaButton
                      onClick={this.handleDeleteColumn}
                      color="danger"
                      type="secondary"
                      size="sm"
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 4, justifyContent: "center" }}
                      tabIndex={0}
                    >
                      <KIcon iconName="view_week" style={{ fontSize: 16 }} />
                      <span>Col</span>
                    </KuikaButton>
                  </div>
                </KuikaTooltip>
              </div>
              <div style={{ marginTop: 6 }}>
                <KuikaTooltip content="Clear the cell content">
                  <div>
                    <KuikaButton
                      onClick={this.handleDeleteCell}
                      color="danger"
                      type="secondary"
                      size="sm"
                      status={this.statusOf("clearCell")}
                      statusLabels={CONFIRMED_OPERATION_LABELS.clearCell}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}
                      tabIndex={0}
                    >
                      <KIcon iconName="clear" style={{ fontSize: 16 }} />
                      <span>Clear Cell</span>
                    </KuikaButton>
                  </div>
                </KuikaTooltip>
              </div>
            </div>

            <div
              style={{
                padding: "8px 12px",
                background: this.state.mergeSelectionMode ? "var(--blue-05)" : "var(--dark-1)",
                borderRadius: "var(--radius-l)",
                border: this.state.mergeSelectionMode ? "1px solid var(--blue-5)" : "1px solid var(--dark-3)",
                transition: "all 0.2s ease"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                <KIcon iconName="merge" style={{ fontSize: 16, color: "var(--accent-fg)" }} />
                <KuikaTextMedium style={{ fontSize: 12, fontWeight: 600, color: "var(--dark-10)", flex: 1 }}>
                  Merge & Split
                </KuikaTextMedium>
                {this.state.mergeSelectionMode && (
                  <KuikaBadge color="blue" style={{ fontSize: 10, padding: "2px 8px" }}>
                    Active
                  </KuikaBadge>
                )}
              </div>
              <KuikaTooltip
                content={
                  this.state.mergeSelectionMode
                    ? "Exit merge mode and cancel selection"
                    : "Enter merge mode to select and merge multiple cells"
                }
              >
                <div>
                  <KuikaButton
                    onClick={this.handleToggleMergeMode}
                    type={this.state.mergeSelectionMode ? "primary" : "secondary"}
                    size="sm"
                    style={{ width: "100%", display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}
                    tabIndex={0}
                  >
                    <KIcon iconName={this.state.mergeSelectionMode ? "close" : "merge_type"} style={{ fontSize: 16 }} />
                    <span>{this.state.mergeSelectionMode ? "Exit Mode" : "Merge Mode"}</span>
                  </KuikaButton>
                </div>
              </KuikaTooltip>
              {this.state.mergeSelectionMode && (
                <div
                  style={{
                    marginTop: 8,
                    padding: 8,
                    background: "var(--white)",
                    borderRadius: "var(--radius-m)",
                    border: "1px solid var(--dark-3)"
                  }}
                >
                  <KuikaTooltip
                    content={isColumnSelected ? "Remove this cell from selection" : "Add this cell to selection"}
                  >
                    <div>
                      <KuikaButton
                        onClick={this.handleSelectColumnForMerge}
                        type={isColumnSelected ? "primary" : "secondary"}
                        size="sm"
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          justifyContent: "center"
                        }}
                        tabIndex={0}
                      >
                        <KIcon
                          iconName={isColumnSelected ? "check_box" : "check_box_outline_blank"}
                          style={{ fontSize: 16 }}
                        />
                        <span>{isColumnSelected ? "Selected" : "Select Cell"}</span>
                      </KuikaButton>
                    </div>
                  </KuikaTooltip>
                  <div
                    style={{
                      marginTop: 8,
                      padding: "6px 8px",
                      background: "var(--dark-1)",
                      borderRadius: "var(--radius-s)",
                      display: "flex",
                      alignItems: "center",
                      gap: 6
                    }}
                  >
                    <KIcon iconName="check_circle" style={{ fontSize: 14, color: "var(--accent-fg)" }} />
                    <span style={{ fontSize: 11, color: "var(--dark-8)", flex: 1 }}>
                      {this.state.selectedColumnsForMerge.length} cell
                      {this.state.selectedColumnsForMerge.length !== 1 ? "s" : ""} selected
                    </span>
                  </div>
                  <KuikaTooltip
                    content={
                      this.state.selectedColumnsForMerge.length < 2
                        ? "Select at least 2 cells to merge"
                        : "Merge all selected cells into one"
                    }
                  >
                    <div style={{ marginTop: 6 }}>
                      <KuikaButton
                        onClick={this.handleMergeSelectedColumns}
                        disabled={this.state.selectedColumnsForMerge.length < 2}
                        type="secondary"
                        size="sm"
                        status={this.statusOf("merge")}
                        statusLabels={CONFIRMED_OPERATION_LABELS.merge}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          justifyContent: "center"
                        }}
                        tabIndex={0}
                      >
                        <KIcon iconName="call_merge" style={{ fontSize: 16 }} />
                        <span>Merge Cells</span>
                      </KuikaButton>
                    </div>
                  </KuikaTooltip>
                </div>
              )}
              <div style={{ marginTop: 6 }}>
                <KuikaTooltip content="Unmerge the selected merged cell">
                  <div>
                    <KuikaButton
                      onClick={this.handleUnmergeColumns}
                      type="secondary"
                      size="sm"
                      status={this.statusOf("unmerge")}
                      statusLabels={CONFIRMED_OPERATION_LABELS.unmerge}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}
                      tabIndex={0}
                    >
                      <KIcon iconName="unfold_more" style={{ fontSize: 16 }} />
                      <span>Unmerge Cell</span>
                    </KuikaButton>
                  </div>
                </KuikaTooltip>
              </div>
              {/* <div style={{ marginTop: 6 }}>
                <KuikaTooltip content="Split the current merged cell">
                  <div>
                    <KuikaButton
                      onClick={this.handleSplitCell}
                      type="secondary"
                      size="sm"
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}
                      tabIndex={0}
                    >
                      <KIcon iconName="call_split" style={{ fontSize: 16 }} />
                      <span>Split Cell</span>
                    </KuikaButton>
                  </div>
                </KuikaTooltip>
              </div> */}
            </div>
          </div>
        ),
        forceRender: true
      }
    ];

    return (
      <div className="kuika-right-panel__card--wrapper" style={{ marginTop: 8, marginBottom: 8 }}>
        <KuikaCollapsePanel
          items={items}
          defaultActiveKey={["staticTablePanel"]}
          expandIconPosition="right"
          forceRender
        />
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock
    ) {
      return true;
    }
    return false;
  };
}

const staticTableOperationsPanel = withSD(StaticTableOperationsPanel);
export { staticTableOperationsPanel as StaticTableOperationsPanel };
